<template>
  <div class="flex flex-col lg:flex-row min-h-screen">
    <!-- Left Section with Images - Hidden on mobile -->
    <div class="hidden lg:block w-1/2 bg-gray-50 p-12 relative">
      <div class="absolute top-8 left-12 flex items-center">
        <router-link to="/" class="flex items-center cursor-pointer">
          <img
            src="../assets/optimaAI.png"
            alt="OptimaAI Logo"
            class="h-8 w-8 mr-2"
          />
          <h3 class="text-xl font-semibold text-gray-900">OptimaAI Creator</h3>
        </router-link>
      </div>

      <div class="h-full flex items-start justify-center pt-12">
        <div
          class="relative w-[450px] h-[250px] flex items-center justify-center mb-16"
        >
          <!-- First image (left) -->
          <div class="absolute left-[5%] top-[45%] -translate-y-1/2 w-52 h-52">
            <img
              src="../assets/account1.png"
              class="w-full h-full rounded-full object-cover shadow-lg border-4 border-white"
              alt="Profile 1"
            />
          </div>

          <!-- Second image (middle-right) -->
          <div class="absolute left-[50%] top-[92%] -translate-y-2/3 w-40 h-40">
            <img
              src="../assets/account2.png"
              class="w-full h-full rounded-full object-cover shadow-lg border-4 border-white"
              alt="Profile 2"
            />
          </div>

          <!-- Third image (bottom-middle) -->
          <div class="absolute left-[20%] top-[90%] w-36 h-36">
            <img
              src="../assets/account3.png"
              class="w-full h-full rounded-full object-cover shadow-lg border-4 border-white"
              alt="Profile 3"
            />
          </div>
        </div>
      </div>

      <div class="absolute bottom-12 left-12">
        <div class="max-w-[400px] mt-8">
          <h2 class="text-xl font-bold text-gray-800 leading-tight mb-2">
            Join our community of creators
          </h2>
          <p class="text-gray-500 text-sm leading-relaxed">
            Start your journey with OptimaAI Creator.<br />
            Create, share, and grow your audience with our powerful tools.
          </p>
        </div>
      </div>
    </div>

    <!-- Right Section with Signup Form -->
    <div
      class="w-full lg:w-1/2 min-h-screen flex flex-col items-center justify-center lg:justify-start p-4 lg:p-8"
    >
      <!-- Mobile Header -->
      <div class="lg:hidden flex items-center mb-8">
        <img src="../assets/optimaAI.png" alt="" class="h-8 w-8 mr-2" />
        <h3 class="text-xl font-semibold text-gray-900">OptimaAI Creator</h3>
      </div>

      <div class="w-full max-w-[450px] px-4">
        <h1 class="text-xl lg:text-xl font-bold mb-2">Create your account</h1>
        <p class="text-gray-600 mb-6 text-sm">
          Join OptimaAI Creator and start your creator journey
        </p>

        <form @submit.prevent="handleSignup" class="space-y-4">
          <div class="space-y-3">
            <!-- First Name -->
            <div class="flex gap-2">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >First Name</label
                >
                <div class="relative">
                  <div
                    class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5 text-gray-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        d="M10 10a4 4 0 100-8 4 4 0 000 8zm-7 8a7 7 0 1114 0H3z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    v-model="firstName"
                    @input="onNameInput('firstName', $event)"
                    placeholder="First name"
                    class="w-full pl-10 pr-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <!-- Last Name -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Last Name</label
                >
                <div class="relative">
                  <div
                    class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  ></div>
                  <input
                    type="text"
                    v-model="lastName"
                    @input="onNameInput('lastName', $event)"
                    placeholder="Last name"
                    class="w-full pl-3 pr-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Email address</label
              >

              <div class="relative">
                <!-- Left Email Icon -->
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"
                    />
                    <path
                      d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"
                    />
                  </svg>
                </div>

                <!-- Email Input -->
                <input
                  type="email"
                  v-model="email"
                  @input="onEmailInput"
                  @keydown.space.prevent="preventLeadingSpace"
                  placeholder="Enter your email"
                  :class="[
                    'w-full pl-10 pr-10 py-1.5 border rounded focus:outline-none focus:ring-1',
                    isEmailValid === true
                      ? 'border-green-500 focus:ring-green-500'
                      : isEmailValid === false
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-blue-500',
                  ]"
                  required
                />

                <!-- Right Side Icons (Green Tick / Red Error) -->

                <!-- Green Tick -->
                <div
                  v-if="isEmailValid === true"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-green-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3.25-3.25a1 1 0 111.414-1.414L8.5 11.086l6.543-6.543a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>

                <!-- Red Error Cross -->
                <div
                  v-if="isEmailValid === false"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-red-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.536-10.464a1 1 0 00-1.414-1.414L10 8.586 7.879 6.464a1 1 0 00-1.414 1.414L8.586 10l-2.121 2.121a1 1 0 101.414 1.414L10 11.414l2.121 2.121a1 1 0 001.414-1.414L11.414 10l2.122-2.122z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Country Dropdown -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Country</label
              >
              <CountryAutocomplete
                v-model="country"
                @select="handleCountrySelect"
              />
            </div>

            <!-- Zip/PostalCode -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Zip/Postcode</label
              >
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-400"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  v-model="postCode"
                  @input="trimInput('postCode')"
                  placeholder="Enter your zip/post-code"
                  class="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <!-- OnlyFans Username -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >OnlyFans Username</label
              >
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  v-model="onlyFansUsername"
                  @input="trimInput('onlyFansUsername')"
                  placeholder="Enter your OnlyFans username"
                  class="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <p class="text-xs text-gray-500 mt-1">
                Your OnlyFans username will be verified automatically
              </p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Password</label
              >
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  :type="showPassword ? 'text' : 'password'"
                  v-model="password"
                  @input="trimInput('password')"
                  placeholder="Enter your password"
                  class="w-full pl-10 pr-10 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  <svg
                    v-if="!showPassword"
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Confirm Password</label
              >
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  v-model="confirmPassword"
                  placeholder="Confirm your password"
                  class="w-full pl-10 pr-10 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  <svg
                    v-if="!showConfirmPassword"
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- <div v-if="error" class="text-red-500 text-sm text-center">
                        {{ error }}
                    </div> -->

          <button
            type="submit"
            class="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded transition duration-200 flex items-center justify-center"
            :disabled="isLoading"
          >
            <svg
              v-if="isLoading"
              class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ isLoading ? "Creating Account..." : "Create Account" }}
          </button>

          <p class="text-center text-gray-600 text-sm">
            Already have an account?
            <a
              @click="$router.push('/login')"
              class="text-blue-600 hover:text-blue-800 cursor-pointer"
              >Log in</a
            >
          </p>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { useToast } from "vue-toastification/dist/index.mjs";
import CountryAutocomplete from "./CountryAutocomplete.vue";

export default {
  components: { CountryAutocomplete },
  name: "Signup-component",
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      email: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
      country: "",
      postCode: "",
      onlyFansUsername: "",
      //   displayName: "",
      error: null,
      isEmailValid: null,
      firstNameValid: null,
      lastNameValid: null,
      showPassword: false,
      showConfirmPassword: false,
      isLoading: false,
    };
  },
  methods: {
    trimInput(field) {
      this[field] = this[field].replace(/^\s+|\s+$/g, "");
      this[field] = this[field].replace(/\s{2,}/g, " ");
    },

    // handleCountrySelect(option) {
    //   console.log("Country object:", option);
    // },

    validateEmail() {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (this.email === "") {
        this.isEmailValid = null;
      } else {
        this.isEmailValid = regex.test(this.email);
      }
    },

    preventLeadingSpace() {
      if (!this.email || this.email.length === 0) {
        return false;
      }
    },

    onEmailInput(e) {
      let value = e.target.value;

      value = value.replace(/^\s+/, "");
      value = value.replace(/\s{2,}/g, " ");
      value = value.replace(/\s+$/, "");

      this.email = value;

      this.validateEmail();
    },

    validateName(field) {
      this.trimInput(field);

      const nameRegex = /^[A-Za-z\s]+$/;

      if (this[field] === "") {
        this[`${field}Valid`] = null;
      } else {
        this[`${field}Valid`] = nameRegex.test(this[field]);
      }
    },

    onNameInput(field, event) {
      let value = event.target.value;

      value = value.replace(/[^A-Za-z\s]/g, "");
      value = value.replace(/\s{2,}/g, " ");

      this[field] = value;
      this.validateName(field);
    },

    async handleSignup() {
      try {
        this.error = null;
        this.isLoading = true;
        if (this.password !== this.confirmPassword) {
          // this.error = 'Passwords do not match';
          this.toast.error("Passwords do not match");
          return;
        }

        if (
          !this.email &&
          !this.firstName &&
          !this.lastName &&
          !this.postCode &&
          !this.country &&
          !this.password &&
          !this.onlyFansUsername
        ) {
          this.toast.error("Required field is missing");
          return;
        }

        const apiUrl = process.env.VUE_APP_API_URL || "http://localhost:5000";
        const response = await axios.post(`${apiUrl}/api/auth/signup`, {
          email: this.email,
          firstName: this.firstName,
          lastName: this.lastName,
          postCode: this.postCode,
          country: this.country,
          password: this.password,
          onlyFansUsername: this.onlyFansUsername,
          //   firstName: this.displayName,
        });

        if (response.data.success) {
          // Save user data to localStorage
          if (response.data.user) {
            const dataToStore = {
              creater: response.data.user, // save the full user object under 'creater'
            };

            localStorage.setItem("data", JSON.stringify(dataToStore));
            localStorage.setItem("token", response.data.token);
            console.log("User data saved:", dataToStore);
          }
          this.toast.success("Signup successful! Redirecting...");
          setTimeout(() => {
            this.$router.push("/welcome");
          }, 1500);
        } else {
          throw new Error("Signup failed");
        }
      } catch (error) {
        // console.error(error.response?.data?.message || error.message || 'An error occurred during signup');
        this.toast.error(
          error.response?.data?.message ||
            error.message ||
            "An error occurred during signup"
        );
        // this.error = error.response?.data?.message || error.message || 'An error occurred during signup';
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Add any additional custom styles here */
button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
